// Unit health bars (GDD §14, docs/ART-DIRECTION.md §2.5: HP is among the brightest elements): one camera-facing quad
// per unit, filled to its HP fraction in the owner's colour over a dim track. The GlyphLayer places and sizes them
// with its glyphs every frame (begin → add… → end); one instanced draw call, on the emissive layer, additive and
// depth-tested so the globe hides bars on its far side. Nothing allocates per frame; a frame with more bars than the
// buffers hold grows them (growth.ts).

import {
  AdditiveBlending,
  BufferAttribute,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  ShaderMaterial,
  type Color,
  type Vector3,
} from 'three'
import { EMISSIVE_LAYER, assignLayer } from '../layers'
import { grownCapacity, grownInstanced } from './growth'
import { uploadPrefix } from './LineBatch'

/** Brightness of the empty part of a bar, relative to its fill. */
const TRACK_BRIGHTNESS = 0.2
const BAR_INTENSITY = 1.1

const VEC2 = 2
const VEC3 = 3

/** A unit quad around the origin, with u running left to right along the bar. */
const QUAD_POSITIONS = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0])
const QUAD_UVS = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1])
const QUAD_INDEX = [0, 1, 2, 0, 2, 3]

const BAR_VERTEX = /* glsl */ `
attribute vec3 aCentre;
attribute vec2 aSize;
attribute float aFill;
attribute vec3 aColour;

varying float vU;
varying float vFill;
varying vec3 vColour;

void main() {
  vec4 view = modelViewMatrix * vec4(aCentre, 1.0);
  // Camera-facing: the quad spreads in view space, so it reads the same from any orbit angle.
  view.xy += position.xy * aSize;
  vU = uv.x;
  vFill = aFill;
  vColour = aColour;
  gl_Position = projectionMatrix * view;
}
`

const BAR_FRAGMENT = /* glsl */ `
uniform float uTrack;
uniform float uIntensity;

varying float vU;
varying float vFill;
varying vec3 vColour;

void main() {
  float filled = step(vU, vFill);
  gl_FragColor = vec4(vColour * mix(uTrack, 1.0, filled) * uIntensity, 1.0);
}
`

export class HealthBars {
  readonly mesh: Mesh
  private readonly geometry = new InstancedBufferGeometry()
  private readonly material: ShaderMaterial
  private centres: InstancedBufferAttribute
  private sizes: InstancedBufferAttribute
  private fills: InstancedBufferAttribute
  private colours: InstancedBufferAttribute
  private count = 0

  constructor(private capacity: number) {
    this.geometry.setAttribute('position', new BufferAttribute(QUAD_POSITIONS, VEC3))
    this.geometry.setAttribute('uv', new BufferAttribute(QUAD_UVS, VEC2))
    this.geometry.setIndex(QUAD_INDEX)
    this.centres = this.instanced(VEC3, 'aCentre')
    this.sizes = this.instanced(VEC2, 'aSize')
    this.fills = this.instanced(1, 'aFill')
    this.colours = this.instanced(VEC3, 'aColour')
    this.geometry.instanceCount = 0
    this.material = new ShaderMaterial({
      uniforms: { uTrack: { value: TRACK_BRIGHTNESS }, uIntensity: { value: BAR_INTENSITY } },
      vertexShader: BAR_VERTEX,
      fragmentShader: BAR_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })
    this.mesh = new Mesh(this.geometry, this.material)
    // Bars move every frame; a cached bounding sphere would cull them wrongly.
    this.mesh.frustumCulled = false
    assignLayer(this.mesh, EMISSIVE_LAYER)
  }

  begin(): void {
    this.count = 0
  }

  /** A bar centred at `centre` (world), `width` × `height` render units, filled to `fill` (0..1). */
  add(centre: Vector3, width: number, height: number, fill: number, colour: Color): void {
    if (this.count >= this.capacity) this.grow(this.count + 1)
    const slot = this.count++
    this.centres.setXYZ(slot, centre.x, centre.y, centre.z)
    this.sizes.setXY(slot, width, height)
    this.fills.setX(slot, Math.min(1, Math.max(0, fill)))
    this.colours.setXYZ(slot, colour.r, colour.g, colour.b)
  }

  end(): void {
    this.geometry.instanceCount = this.count
    if (this.count === 0) return
    uploadPrefix(this.centres, this.count * VEC3)
    uploadPrefix(this.sizes, this.count * VEC2)
    uploadPrefix(this.fills, this.count)
    uploadPrefix(this.colours, this.count * VEC3)
  }

  dispose(): void {
    this.geometry.dispose()
    this.material.dispose()
  }

  /** Doubles the per-bar buffers to hold `needed` bars, keeping the bars this frame already wrote. */
  private grow(needed: number): void {
    const capacity = grownCapacity(this.capacity, needed)
    // Frees the GPU copies of the current buffers; the next render uploads the grown ones.
    this.geometry.dispose()
    this.centres = this.regrown(this.centres, 'aCentre', capacity)
    this.sizes = this.regrown(this.sizes, 'aSize', capacity)
    this.fills = this.regrown(this.fills, 'aFill', capacity)
    this.colours = this.regrown(this.colours, 'aColour', capacity)
    this.capacity = capacity
  }

  private regrown(attribute: InstancedBufferAttribute, name: string, capacity: number): InstancedBufferAttribute {
    const grown = grownInstanced(attribute, capacity, this.count)
    this.geometry.setAttribute(name, grown)
    return grown
  }

  private instanced(itemSize: number, name: string): InstancedBufferAttribute {
    const attribute = new InstancedBufferAttribute(new Float32Array(this.capacity * itemSize), itemSize).setUsage(DynamicDrawUsage)
    this.geometry.setAttribute(name, attribute)
    return attribute
  }
}
